import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getHealthSafetyData, GetHealthSafetyDataOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Flame, CheckCircle2, AlertTriangle, XCircle, Clock, MapPin, Phone, TriangleAlert, ShieldCheck, Heart, Zap, DoorOpen } from 'lucide-react';
import { format, parseISO, isBefore, addDays } from 'date-fns';

type Item = GetHealthSafetyDataOutputType['items'][0];

const FIRE_TIPS = [
  { icon: TriangleAlert, color: 'text-red-500', title: 'On discovering a fire', text: 'Raise the alarm immediately. Do NOT attempt to fight a large fire. Evacuate calmly.' },
  { icon: DoorOpen, color: 'text-orange-500', title: 'On hearing the fire alarm', text: 'Leave immediately by the nearest fire exit. Do not use the lift. Do not stop to collect belongings.' },
  { icon: MapPin, color: 'text-green-600', title: 'Assembly point', text: 'Assemble at the designated assembly point. Do not re-enter until told it is safe.' },
  { icon: Phone, color: 'text-blue-600', title: 'Call 999', text: 'Once safe, call 999. Give the address: Church Street, Sherston, SN16 0LH.' },
];

const TYPE_ICONS: Record<string, React.ReactNode> = {
  'Fire Extinguisher': <Flame className="w-4 h-4 text-red-500" />,
  'Fire Exit': <DoorOpen className="w-4 h-4 text-green-600" />,
  'First Aid Kit': <Heart className="w-4 h-4 text-red-500" />,
  'Assembly Point': <MapPin className="w-4 h-4 text-green-600" />,
  'Fire Alarm Panel': <Zap className="w-4 h-4 text-yellow-500" />,
  'Emergency Light': <Zap className="w-4 h-4 text-yellow-400" />,
};

const STATUS_CFG = {
  Good: { cls: 'bg-green-500/10 text-green-700 border-green-500/30', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  'Due Soon': { cls: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30', icon: <Clock className="w-3.5 h-3.5" /> },
  Overdue: { cls: 'bg-orange-500/10 text-orange-700 border-orange-500/30', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
  Faulty: { cls: 'bg-red-500/10 text-red-700 border-red-500/30', icon: <XCircle className="w-3.5 h-3.5" /> },
};

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

function ItemCard({ item }: { item: Item }) {
  const cfg = STATUS_CFG[item.status as keyof typeof STATUS_CFG] || STATUS_CFG.Good;
  return (
    <div className="border border-border rounded-lg p-3 flex items-start gap-3">
      <div className="w-8 h-8 bg-muted/60 rounded-lg flex items-center justify-center shrink-0">
        {TYPE_ICONS[item.type || ''] || <ShieldCheck className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground text-sm">{item.itemName}</span>
          <Badge variant="outline" className={`text-xs flex items-center gap-1 ${cfg.cls}`}>{cfg.icon}{item.status}</Badge>
        </div>
        {item.locationDescription && <p className="text-xs text-muted-foreground mt-0.5"><MapPin className="w-3 h-3 inline mr-1" />{item.locationDescription}</p>}
        {item.roomName && <p className="text-xs text-muted-foreground mt-0.5">{item.roomName}</p>}
        {item.nextInspection && <p className="text-xs text-muted-foreground mt-0.5">Next inspection: {fmtDate(item.nextInspection)}</p>}
      </div>
    </div>
  );
}

export default function HealthSafety() {
  const { user } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [statusCounts, setStatusCounts] = useState({ good: 0, dueSoon: 0, overdue: 0, faulty: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getHealthSafetyData({}).then(d => { setItems(d.items); setStatusCounts(d.statusCounts); }).finally(() => setLoading(false));
  }, [user]);

  const grouped = items.reduce((acc, item) => {
    const type = item.type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {} as Record<string, Item[]>);

  const hasIssues = statusCounts.overdue + statusCounts.faulty > 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6 max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center">
              <Flame className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Health &amp; Fire Safety</h1>
              <p className="text-sm text-muted-foreground">Fire safety equipment, evacuation info &amp; emergency procedures</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-4xl mx-auto space-y-8">

        {/* Status banner */}
        <div className={`rounded-xl border p-4 flex items-center gap-3 ${hasIssues ? 'bg-orange-500/10 border-orange-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
          {hasIssues
            ? <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0" />
            : <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />}
          <div>
            <p className={`font-semibold text-sm ${hasIssues ? 'text-orange-700' : 'text-green-700'}`}>
              {hasIssues ? `Action required: ${statusCounts.overdue} overdue, ${statusCounts.faulty} faulty item${statusCounts.faulty !== 1 ? 's' : ''}` : 'All fire safety equipment is up to date'}
            </p>
            <p className={`text-xs mt-0.5 ${hasIssues ? 'text-orange-600' : 'text-green-600'}`}>
              {statusCounts.good} Good · {statusCounts.dueSoon} Due Soon · {statusCounts.overdue} Overdue · {statusCounts.faulty} Faulty
            </p>
          </div>
        </div>

        {/* Fire action tips */}
        <div>
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">In Case of Fire</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIRE_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="border border-border bg-card rounded-xl p-4 flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <Icon className={`w-4 h-4 ${tip.color}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground text-sm">{tip.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{tip.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Equipment register */}
        <div>
          <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">Safety Equipment Register</h2>
          {loading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl p-10 text-center text-muted-foreground">
              <ShieldCheck className="w-8 h-8 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No safety equipment has been logged yet. Contact your manager.</p>
            </div>
          ) : (
            (Object.entries(grouped) as [string, Item[]][]).map(([type, typeItems]) => (
              <div key={type} className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  {TYPE_ICONS[type] || <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground" />}
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{type}</p>
                  <span className="text-xs text-muted-foreground">({typeItems.length})</span>
                </div>
                <div className="space-y-2">
                  {typeItems.map(item => <ItemCard key={item.id} item={item} />)}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Emergency numbers */}
        <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-5">
          <p className="text-xs font-bold text-destructive uppercase tracking-wider mb-3">Emergency Contacts</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <a href="tel:999" className="flex items-center gap-2 text-sm font-bold text-foreground hover:text-primary"><Phone className="w-4 h-4 text-red-500" /> 999 — Emergency</a>
            <a href="tel:101" className="flex items-center gap-2 text-sm text-foreground hover:text-primary"><Phone className="w-4 h-4 text-muted-foreground" /> 101 — Non-emergency Police</a>
            <a href="tel:111" className="flex items-center gap-2 text-sm text-foreground hover:text-primary"><Phone className="w-4 h-4 text-muted-foreground" /> 111 — NHS Non-emergency</a>
          </div>
        </div>
      </div>
    </div>
  );
}
